import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { APP_THEME } from "../theme/theme";
import Header from "../components/header";
import toast, { Toaster } from 'react-hot-toast';
import { apiCall } from "../utils/api";

const operators = ["Airtel", "Jio", "Vi", "BSNL", "MTNL"];
const circles = [
  "Andhra Pradesh", "Assam", "Bihar & Jharkhand", "Chennai", "Delhi NCR",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jammu & Kashmir", "Karnataka",
  "Kerala", "Kolkata", "Madhya Pradesh & Chhattisgarh", "Maharashtra & Goa",
  "Mumbai", "North East", "Odisha", "Punjab", "Rajasthan", "Tamil Nadu",
  "Uttar Pradesh East", "Uttar Pradesh West & Uttarakhand", "West Bengal"
];

function MobileRecharge() {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [showPlans, setShowPlans] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const apiCallRef = useRef(null);
  const [loadingRecentTr, setLoadingRecentTr] = useState(false);
  const [transactionHistorys, setTransactionHistory] = useState([]);

  useEffect(() => {
    const fetchRecentHistory = async () => {
      setLoadingRecentTr(true);
      try {
        const result = await apiCall("recent-history",
          {
            category: '2',
          },
          true
        );

        if (result.status === true && result.history) {
          setTransactionHistory(result.history);
        } else {
          toast.error(result.message || "An error occurred. Please try again.");
          setTransactionHistory([]);
        }
      } catch (error) {
        toast.error("Network error. Please try again.");
        setTransactionHistory([]);
      } finally {
        setLoadingRecentTr(false);
      }
    }
    fetchRecentHistory();
  }, [])

  // Get only top 3 recent successful recharges
  const recentRecharges = transactionHistorys
    .filter(t => t.order_type === "2")
    .slice(0, 3);

  // Auto-fetch details when mobile number reaches 10 digits
  useEffect(() => {
    if (apiCallRef.current) {
      clearTimeout(apiCallRef.current);
    }

    if (mobile && mobile.length === 10 && !isLoading) {
      apiCallRef.current = setTimeout(() => {
        fetchMobileDetails();
      }, 500);
    }

    return () => {
      if (apiCallRef.current) {
        clearTimeout(apiCallRef.current);
      }
    };
  }, [mobile]);

  const fetchMobileDetails = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setError("");

    try {
      const result = await apiCall(
        "mdm-fetch",
        {
          number: mobile
        },
        true
      );

      if (result.status === true) {
        setShowPlans(true);
        navigate('/mobile-recharge-plan', {
          state: {
            number: mobile,
            circle: result.operator_data.circle,
            circle_id: result.operator_data.circle_id,
            operator: result.operator_data.operator,
            operator_id: result.operator_data.operator_id,
            operator_logo: result.operator_data.operator_logo,
          }
        });
      } else {
        toast.error(result.message || "An error occurred. Please try again.");
        setIsLoading(false);
      }
    } catch (error) {
      setError("Network error. Please try again.");
      toast.error("Network error. Please try again.");
      setIsLoading(false);
    }
  };

  const handleMobileChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobile(val);
    setError("");
    if (val.length !== 10) {
      setShowPlans(false);
      setIsLoading(false);
    }
  };

  const handleContactClick = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "GET_CONTACT_INFO"
        })
      );
    } else {
      toast.error("Contact picker not available");
    }
  };

  // Receive contact from React Native WebView
  useEffect(() => {
    const handleContactData = (event) => {
      try {
        let data = event.data;
        // Convert string JSON to object
        if (typeof data === "string") {
          data = JSON.parse(data);
        }
        if (data?.number) {
          const cleanNumber = data.number
            .replace(/\D/g, '')
            .slice(-10);

          if (/^\d{10}$/.test(cleanNumber)) {
            setMobile(cleanNumber);
          } else {
            console.warn("Invalid contact number");
          }
        }
      } catch (err) {
        console.log("Contact parse error", err);
      }
    };

    window.addEventListener("message", handleContactData);

    return () => {
      window.removeEventListener("message", handleContactData);
    };

  }, []);

  const handleProcessPayment = async () => {
    if (isLoading) return;

    if (!mobile || mobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number', {
        duration: 2000,
        position: 'bottom-center'
      });
      return;
    }

    await fetchMobileDetails();
  };

  const isValidMobile = mobile && mobile.length === 10;

  // Format amount with ₹ symbol
  const formatAmount = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  // Render skeleton loader for recent transactions
  const renderSkeleton = () => {
    return (
      <div className="flex flex-col gap-2.5">
        {[1, 2, 3].map((index) => (
          <div
            key={`skeleton-${index}`}
            className="flex items-center gap-3 p-3.5 rounded-xl border animate-pulse"
            style={{
              background: APP_THEME.surfaceColor,
              borderColor: APP_THEME.borderColor,
            }}
          >
            <div
              className="w-8 h-8 rounded-xl"
              style={{
                background: APP_THEME.borderColor,
                opacity: 0.3
              }}
            />
            <div className="flex-1">
              <div
                className="h-4 rounded w-32 mb-2"
                style={{
                  background: APP_THEME.borderColor,
                  opacity: 0.3
                }}
              />
              <div
                className="h-3 rounded w-24"
                style={{
                  background: APP_THEME.borderColor,
                  opacity: 0.2
                }}
              />
            </div>
            <div
              className="h-4 w-4 rounded"
              style={{
                background: APP_THEME.borderColor,
                opacity: 0.3
              }}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <Header title={"Mobile Recharge"} showBack={true} />
      <div className="p-4 pb-24">
        {/* Mobile Input with Icon */}
        <div className="mb-7">
          <label
            className="block text-sm font-semibold mb-2 pl-1"
            style={{ color: APP_THEME.textPrimary }}
          >
            Mobile Number
          </label>
          <div
            className="relative flex items-center rounded-xl transition-all duration-200"
            style={{
              border: `1px solid ${error ? APP_THEME.errorColor : APP_THEME.borderColor}`,
              background: APP_THEME.surfaceColor,
            }}
          >
            <div className="pl-4 text-[15px] font-semibold" style={{ color: APP_THEME.textSecondary }}>
              +91
            </div>
            <input
              type="tel"
              maxLength={10}
              placeholder="Enter 10-digit number"
              value={mobile}
              onChange={handleMobileChange}
              className="flex-1 p-3.5 px-3 border-none text-base font-semibold outline-none bg-transparent"
              style={{ color: APP_THEME.textPrimary }}
              onFocus={(e) => (e.target.parentElement.style.borderColor = APP_THEME.primaryColor)}
              onBlur={(e) => (e.target.parentElement.style.borderColor = error ? APP_THEME.errorColor : APP_THEME.borderColor)}
              disabled={isLoading}
            />
            <div
              className="pr-3 flex items-center cursor-pointer hover:opacity-70 transition-opacity"
              onClick={handleContactClick}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
                <line x1="20" y1="8" x2="20" y2="14" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
            </div>
          </div>
          {error && (
            <div className="text-xs mt-1 pl-1" style={{ color: APP_THEME.errorColor }}>
              {error}
            </div>
          )}
        </div>

        {/* Recent Recharges (Max 3) */}
        <div>
          <h3
            className="text-sm font-bold mb-4 pl-1"
            style={{ color: APP_THEME.textPrimary }}
          >
            Recent Recharges
          </h3>

          {loadingRecentTr ? (
            renderSkeleton()
          ) : recentRecharges.length > 0 ? (
            <div className="flex flex-col gap-2.5">
              {recentRecharges.map((txn) => (
                <div
                  key={txn.id}
                  onClick={() => {
                    if (!isLoading && txn.cn) {
                      setMobile(txn.cn);
                      setShowPlans(true);
                      setError("");
                    }
                  }}
                  className="flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-200 active:scale-98"
                  style={{
                    background: APP_THEME.surfaceColor,
                    borderColor: APP_THEME.borderColor,
                    opacity: isLoading ? 0.6 : 1,
                    pointerEvents: isLoading ? 'none' : 'auto'
                  }}
                >
                  {/* Logo from API - 8x8 */}
                  {txn.logo ? (
                    <img
                      src={txn.logo}
                      alt={txn.biller_name}
                      className="w-8 h-8 rounded-xl object-contain"
                      style={{
                        background: APP_THEME.primaryLight,
                        padding: '4px'
                      }}
                      onError={(e) => {
                        // Fallback to initials if logo fails to load
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}

                  {/* Fallback to initials if no logo or logo fails - 8x8 */}
                  <div
                    className="w-8 h-8 rounded-xl items-center justify-center text-sm font-bold"
                    style={{
                      background: APP_THEME.primaryLight,
                      color: APP_THEME.primaryColor,
                      display: txn.logo ? 'none' : 'flex'
                    }}
                  >
                    {txn.biller_name ? txn.biller_name.charAt(0) : "R"}
                  </div>

                  <div className="flex-1">
                    <div className="text-sm font-bold" style={{ color: APP_THEME.textPrimary }}>
                      {txn.biller_name || "Unknown"} · {txn.cn || "N/A"}
                    </div>
                    <div className="text-[11px]" style={{ color: APP_THEME.textSecondary }}>
                      {txn.time || ""}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs mt-0.5" style={{ color: APP_THEME.textSecondary }}>
                      {formatAmount(txn.amount)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="text-center py-8 rounded-xl border"
              style={{
                color: APP_THEME.textSecondary,
                borderColor: APP_THEME.borderColor,
                background: APP_THEME.surfaceColor
              }}
            >
              <svg className="mx-auto mb-2" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.borderColor} strokeWidth="1.5">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
              <div className="text-sm">No recent recharges found</div>
            </div>
          )}
        </div>
      </div>

      {/* Process Button - Fixed at bottom */}
      {isValidMobile && (
        <div className="fixed bottom-0 left-0 right-0 p-4 border-t shadow-lg" style={{
          background: APP_THEME.surfaceColor,
          borderTopColor: APP_THEME.borderColor
        }}>
          <button
            onClick={handleProcessPayment}
            disabled={isLoading}
            className="w-full py-3.5 rounded-xl font-bold text-white transition-all duration-200 active:scale-98 disabled:opacity-70 disabled:cursor-not-allowed"
            style={{
              background: APP_THEME.primaryColor,
              boxShadow: `0 4px 12px ${APP_THEME.primaryColor}40`
            }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Fetching Details...</span>
              </div>
            ) : (
              "Proceed to Recharge"
            )}
          </button>
        </div>
      )}

      <Toaster position="bottom-center" />

      <style>
        {`
          .active\\:scale-98:active {
            transform: scale(0.98);
          }
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
          .animate-pulse {
            animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
        `}
      </style>
    </>
  );
}

export default MobileRecharge;